import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Copy, RefreshCw, Check, CalendarPlus, Library, Trash2, Wand2, ChevronRight, Pencil, Save, MonitorPlay } from "lucide-react";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { DraftActions } from "@/components/DraftActions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = [
  { key: "hook", label: "Hook", desc: "Pembuka yang bikin berhenti scroll" },
  { key: "foreshadow", label: "Foreshadow", desc: "Teaser yang bikin penasaran" },
  { key: "body", label: "Story (Isi Konten)", desc: "Kerangka inti yang mengalir" },
  { key: "cta", label: "Payoff (Call to Action)", desc: "Ajakan penutup yang natural" },
];

const LIB_IMG = "https://images.unsplash.com/photo-1604782206219-3b9576575203?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRlcnJhY290dGElMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4NzU5MjU3MHww&ixlib=rb-4.1.0&q=85";

function buildDraft(topic, sel) {
  const parts = [];
  if (topic) parts.push(`# ${topic}`);
  STEPS.forEach((s) => { if (sel[s.key]) parts.push(`${s.label.toUpperCase()}\n${sel[s.key]}`); });
  return parts.join("\n\n");
}

export default function SusunScript({ onSendToCalendar, onSendToTeleprompter }) {
  const [topic, setTopic] = useState("");
  const [options, setOptions] = useState({});
  const [selected, setSelected] = useState({});
  const [loadingStep, setLoadingStep] = useState(null);
  const [library, setLibrary] = useState([]);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(null);
  const [deleted, setDeleted] = useState(false);
  const [activeTab, setActiveTab] = useState("compose");
  const [profiles, setProfiles] = useState([]);
  const [identityId, setIdentityId] = useState("");
  const [loadedFullText, setLoadedFullText] = useState("");
  const savedSig = useRef("");

  const loadLibrary = () => api.get("/scripts").then((r) => setLibrary(r.data)).catch(() => {});
  useEffect(() => {
    loadLibrary();
    api.get("/identities").then((r) => {
      setProfiles(r.data);
      if (r.data.length > 0) setIdentityId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const hasComponents = STEPS.some((s) => selected[s.key]);
  const draft = hasComponents ? buildDraft(topic, selected) : (loadedFullText || buildDraft(topic, selected));
  const shown = deleted ? "" : (edited != null ? edited : draft);
  const complete = STEPS.every((s) => selected[s.key]);

  const saveToLibrary = async (text) => {
    try { await api.post("/scripts", { topic, ...selected, full_text: text }); loadLibrary(); return true; }
    catch (e) { return false; }
  };
  const copySave = async () => {
    try { await navigator.clipboard.writeText(shown); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) {}
    const ok = await saveToLibrary(shown);
    toast.success(ok ? "Disalin & disimpan ke Library" : "Disalin ke clipboard");
  };
  const finishEdit = async () => {
    setEditing(false);
    const ok = await saveToLibrary(shown);
    toast.success(ok ? "Perubahan disimpan sebagai versi terbaru" : "Perubahan disimpan");
  };
  const deleteDraft = () => {
    if (!window.confirm("Hapus draft ini? Tindakan ini tidak bisa dibatalkan.")) return;
    setDeleted(true); setEditing(false);
    toast.success("Draft dihapus");
  };

  useEffect(() => {
    if (complete && draft && savedSig.current !== draft) {
      savedSig.current = draft;
      const payload = { topic, ...selected, full_text: draft };
      api.post("/scripts", payload).then(() => { loadLibrary(); toast.success("Draft otomatis tersimpan ke Library"); }).catch(() => {});
    }
  }, [complete, draft]); // eslint-disable-line

  const generate = async (step) => {
    if (!topic.trim()) { toast.error("Tulis topik/premis kontennya dulu ya"); return; }
    setLoadedFullText("");
    setDeleted(false); setEdited(null); setEditing(false);
    setLoadingStep(step);
    try {
      const res = await api.post("/script/generate", { component: step, topic, identity_id: identityId || null, selected });
      setOptions((o) => ({ ...o, [step]: res.data.options }));
    } catch (e) {
      toast.error("Yah, ada gangguan pas nyambung ke AI. Coba lagi ya!");
    } finally {
      setLoadingStep(null);
    }
  };

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      toast.success("Draft disalin ke clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Gagal menyalin — coba salin manual ya");
    }
  };

  const deleteScript = async (id) => {
    await api.delete(`/scripts/${id}`);
    loadLibrary();
    toast.success("Draft dihapus");
  };

  const loadFromLibrary = (s) => {
    setTopic(s.topic || "");
    setSelected({ hook: s.hook, foreshadow: s.foreshadow, body: s.body, cta: s.cta });
    setLoadedFullText(s.full_text || "");
    savedSig.current = s.full_text || "";
    setActiveTab("compose");
    toast.success("Draft dimuat ke editor");
  };

  const activeProfile = profiles.find((p) => p.id === identityId);

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sk-brand font-semibold uppercase tracking-wide text-xs mb-2">Buat naskah</p>
          <h1 className="font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Susun Script</h1>
          <p className="text-sk-muted mt-2 max-w-xl leading-relaxed">Pilih DNA brand-mu, lalu generate. Hook, Foreshadow, Story & Payoff disusun sebagai satu kesatuan yang nyambung.</p>
        </div>
        <div className="flex rounded-full border border-sk-line p-1 bg-sk-surface">
          <button data-testid="tab-compose" onClick={() => setActiveTab("compose")} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${activeTab === "compose" ? "bg-sk-brand text-sk-ink" : "text-sk-muted"}`}>Susun</button>
          <button data-testid="tab-library" onClick={() => setActiveTab("library")} className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${activeTab === "library" ? "bg-sk-brand text-sk-ink" : "text-sk-muted"}`}><Library className="h-4 w-4" /> Library ({library.length})</button>
        </div>
      </div>

      {activeTab === "library" ? (
        <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 sm:p-8 shadow-sm">
          {library.length === 0 ? (
            <EmptyState testid="library-empty" image={LIB_IMG} title="Library Skrip masih kosong" subtitle="Begitu kamu selesai nyusun draft (hook sampai CTA), otomatis kesimpan di sini. Mulai bikin mahakaryamu!" />
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {library.map((s) => (
                <div key={s.id} data-testid="library-item" className="group min-w-0 rounded-xl border border-sk-line bg-sk-bg p-4 flex items-start justify-between gap-4 hover:border-sk-surface2 transition-colors">
                  <button onClick={() => loadFromLibrary(s)} className="flex-1 min-w-0 text-left">
                    <p className="font-bold text-sk-cream truncate">{s.topic || "Tanpa judul"}</p>
                    <p className="text-xs text-sk-muted mt-1 line-clamp-2 leading-relaxed break-words">{s.full_text?.replace(/\n/g, " ").slice(0, 140)}</p>
                  </button>
                  <button onClick={() => deleteScript(s.id)} data-testid="delete-script-btn" className="shrink-0 text-sk-muted hover:text-sk-brand p-2 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-5">
            <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 shadow-sm">
              <label className="block text-sm font-semibold text-sk-cream mb-2">Pakai DNA Personal Brand</label>
              {profiles.length > 0 ? (
                <Select value={identityId} onValueChange={setIdentityId}>
                  <SelectTrigger className="w-full bg-sk-bg border-sk-line rounded-xl text-sk-cream mb-4" data-testid="dna-select">
                    <SelectValue placeholder="Pilih DNA brand" />
                  </SelectTrigger>
                  <SelectContent className="bg-sk-surface border-sk-line text-sk-cream">
                    {profiles.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`dna-opt-${p.id}`}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <div className="mb-4 rounded-xl border border-sk-line bg-sk-bg p-3 text-xs text-sk-muted leading-relaxed" data-testid="no-dna-hint">
                  Belum ada DNA brand. Buat dulu di menu <span className="text-sk-brand font-semibold">Identitas Kreator</span> biar hasil script nyambung sama gaya kamu.
                </div>
              )}

              <label className="block text-sm font-semibold text-sk-cream mb-2">Topik / premis konten</label>
              <input
                data-testid="topic-input"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="cth: 5 kebiasaan pagi yang bikin produktif seharian"
                className="w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent"
              />
              {activeProfile && <p className="text-xs text-sk-muted mt-2">Setiap generate otomatis pakai gaya & positioning dari <span className="text-sk-brand font-semibold">{activeProfile.name}</span>.</p>}
            </div>

            {STEPS.map((step, idx) => (
              <div key={step.key} className={`rounded-2xl border bg-sk-surface p-6 shadow-sm ${loadingStep === step.key ? "sk-generating" : "border-sk-line"}`}>
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="flex items-center gap-3">
                    <span className="h-7 w-7 rounded-full bg-sk-brand/12 text-sk-brand font-bold text-sm flex items-center justify-center shrink-0">{idx + 1}</span>
                    <div>
                      <h3 className="font-bold text-sk-cream">{step.label}</h3>
                      <p className="text-xs text-sk-muted">{step.desc}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => generate(step.key)}
                    disabled={loadingStep === step.key}
                    data-testid={`generate-${step.key}-btn`}
                    className="flex items-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold text-sm px-4 py-2 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sk-brandDark disabled:opacity-60 shrink-0"
                  >
                    {loadingStep === step.key ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                    {options[step.key] ? "Ulangi" : "Generate"}
                  </button>
                </div>

                <AnimatePresence>
                  {options[step.key] && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="grid gap-2.5 mt-4">
                      {options[step.key].map((opt, i) => {
                        const isSel = selected[step.key] === opt;
                        return (
                          <button
                            key={i}
                            data-testid={`option-${step.key}-${i}`}
                            onClick={() => { setLoadedFullText(""); setSelected((s) => ({ ...s, [step.key]: opt })); }}
                            className={`text-left rounded-xl border p-3.5 text-sm leading-relaxed transition-colors ${
                              isSel ? "border-sk-brand bg-sk-brand/10 text-sk-cream" : "border-sk-line bg-sk-bg text-sk-muted hover:border-sk-surface2 hover:text-sk-cream"
                            }`}
                          >
                            <div className="flex items-start gap-2.5">
                              <span className={`mt-0.5 h-4 w-4 rounded-full border shrink-0 flex items-center justify-center ${isSel ? "border-sk-brand bg-sk-brand" : "border-sk-muted"}`}>
                                {isSel && <Check className="h-3 w-3 text-sk-ink" />}
                              </span>
                              <span>{opt}</span>
                            </div>
                          </button>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 shadow-sm lg:sticky lg:top-6">
              <h3 className="font-extrabold text-lg text-sk-cream tracking-tight mb-1">Draft Skrip</h3>
              <p className="text-xs text-sk-muted mb-4">Opsi yang kamu pilih otomatis kegabung di sini.</p>

              {shown ? (
                <>
                  {editing ? (
                    <textarea data-testid="draft-edit" value={shown} onChange={(e) => setEdited(e.target.value)} rows={12} className="w-full rounded-xl bg-sk-bg border border-sk-brand p-4 text-sm text-sk-cream resize-none focus:outline-none focus:ring-2 focus:ring-sk-brand leading-relaxed" />
                  ) : (
                    <div data-testid="draft-preview" className="rounded-xl bg-sk-bg border border-sk-line p-4 text-sm text-sk-cream whitespace-pre-wrap leading-relaxed max-h-[45vh] overflow-y-auto">{shown}</div>
                  )}
                  <DraftActions
                    className="mt-4"
                    editing={editing}
                    onToggleEdit={() => (editing ? finishEdit() : setEditing(true))}
                    onCopy={copySave}
                    copied={copied}
                    onDelete={deleteDraft}
                    editTestid="edit-draft-btn"
                    copyTestid="copy-draft-btn"
                    deleteTestid="delete-draft-btn"
                  />
                  <button onClick={() => onSendToCalendar(shown)} data-testid="send-to-calendar-btn" className="w-full mt-2.5 flex items-center justify-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold py-2.5 text-sm hover:bg-sk-brandDark transition-colors">
                    <CalendarPlus className="h-4 w-4" /> Kirim ke Kalender
                  </button>
                  <button onClick={() => onSendToTeleprompter(shown)} data-testid="read-teleprompter-btn" className="w-full mt-2.5 flex items-center justify-center gap-2 rounded-full border border-sk-line py-2.5 text-sm font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors">
                    <MonitorPlay className="h-4 w-4" /> Baca di Teleprompter
                  </button>
                  {complete && !deleted && <p className="text-xs text-sk-muted mt-3 flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-sk-brand" /> Draft lengkap & tersimpan otomatis di Library</p>}
                </>
              ) : (
                <div className="text-center py-10 px-2">
                  <ChevronRight className="h-8 w-8 text-sk-brand/50 mx-auto mb-3" />
                  <p className="text-sk-muted text-sm leading-relaxed">Tulis topik, lalu generate & pilih opsi. Draft skrip kamu bakal muncul di sini.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
